import { BlobServiceClient } from "@azure/storage-blob";
import { message } from "antd";

const accountName = process.env.REACT_APP_STORAGE_ACCOUNT;
const sasToken = process.env.REACT_APP_STORAGE_SAS;

const streamToString = async (readableStream) => {
  return new Promise((resolve, reject) => {
    const chunks = [];
    readableStream.on("data", (data) => {
      chunks.push(data.toString());
    });
    readableStream.on("end", () => {
      resolve(chunks.join(""));
    });
    readableStream.on("error", reject);
  });
};

export const getBlobNames = async (containerName, folderPath) => {
  const blobServiceClient = new BlobServiceClient(
    `https://${accountName}.blob.core.windows.net/?${sasToken}`
  );

  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

  const containerClient = blobServiceClient.getContainerClient(containerName);

  const blobList = [];

  for await (const blob of containerClient.listBlobsFlat({
    prefix: folderPath,
  })) {
    const lastModified = blob.properties?.lastModified;
    if (blob.name.match(/baseline|comparison|real-time|fullPage/)) continue;
    if (lastModified && lastModified < oneMonthAgo) continue;

    blobList.push(blob.name);
  }
  return blobList;
};

export const downloadBlob = async (containerName, blobName) => {
  const blobServiceClient = new BlobServiceClient(
    `https://${accountName}.blob.core.windows.net/?${sasToken}`
  );

  const containerClient = blobServiceClient.getContainerClient(containerName);
  const blobClient = containerClient.getBlobClient(blobName);

  const downloadBlockBlobResponse = await blobClient.download();
  const blob = await downloadBlockBlobResponse.blobBody;

  return new Blob([blob]);
};

export const updateBaselineImageBlobStorage = async (
  containerName,
  imageUrl,
  newBaselineName
) => {
  const blobServiceClient = new BlobServiceClient(
    `https://${accountName}.blob.core.windows.net/?${sasToken}`
  );
  const containerClient = blobServiceClient.getContainerClient(containerName);

  const blobUrl = new URL(imageUrl);
  const blobName = decodeURIComponent(blobUrl.pathname.substring(1));

  const blobClient = containerClient.getBlobClient(blobName);

  const downloadBlockBlobResponse = await blobClient.download();
  const blobContent = await downloadBlockBlobResponse.blobBody;

  const newBlobClient = containerClient.getBlockBlobClient(newBaselineName);
  await newBlobClient.uploadData(blobContent, {
    blobHTTPHeaders: { blobContentType: "image/png" },
  });
};

export const saveCatalogTable = async (containerName, blobName, data) => {
  try {
    const blobServiceClient = new BlobServiceClient(
      `https://${accountName}.blob.core.windows.net/?${sasToken}`
    );
    const containerClient = blobServiceClient.getContainerClient(containerName);
    const blockBlobClient = containerClient.getBlockBlobClient(`${blobName}`);
    await blockBlobClient.upload(
      JSON.stringify(data),
      JSON.stringify(data).length,
      { blobHTTPHeaders: { blobContentType: "application/json" } }
    );
    message.success("Test catalogs saved successfully!");
  } catch (error) {
    console.error("Error saving table data:", error);
  }
};

export const loadCatalogTable = async (containerName, blobName) => {
  try {
    const blobServiceClient = new BlobServiceClient(
      `https://${accountName}.blob.core.windows.net/?${sasToken}`
    );
    const containerClient = blobServiceClient.getContainerClient(containerName);
    const blobClient = containerClient.getBlobClient(`${blobName}`);
    const downloadResponse = await blobClient.download();
    const downloadedData = await streamToString(
      downloadResponse.readableStreamBody
    );
    console.log("downloadedData >> ", JSON.parse(downloadedData));
    return JSON.parse(downloadedData);
  } catch (error) {
    console.error("Error loading table data:", error);
    return [];
  }
};

export const listRealTimeResults = async (containerName, prefix) => {
  try {
    const blobServiceClient = new BlobServiceClient(
      `https://${accountName}.blob.core.windows.net/?${sasToken}`
    );
    const containerClient = blobServiceClient.getContainerClient(containerName);

    let folderMap = new Map();

    for await (const blob of containerClient.listBlobsFlat({ prefix })) {
      const pathParts = blob.name.split("/");

      if (pathParts.length > 2) {
        const folderName = pathParts[2]; // Extract folder name

        if (!folderMap.has(folderName)) {
          folderMap.set(folderName, new Date(blob.properties.createdOn)); // Store the earliest creation time
        }
      }
    }

    const sortedFolders = [...folderMap.entries()]
      .sort((a, b) => b[1] - a[1]) // Sort by date (newest first)
      .map(([name, createdOn]) => ({ name, createdOn })); // Extract just folder names

    return sortedFolders;
  } catch (error) {
    console.error("Error fetching folder names:", error);
    return [];
  }
};

export const createResultsFolder = async (containerName, folderPath) => {
  try {
    const blobServiceClient = new BlobServiceClient(
      `https://${accountName}.blob.core.windows.net/?${sasToken}`
    );
    const containerClient = blobServiceClient.getContainerClient(containerName);

    const folderBlobName = `${folderPath}/testinit.txt`;

    // Create an empty blob to simulate a folder
    const blockBlobClient = containerClient.getBlockBlobClient(folderBlobName);
    await blockBlobClient.upload("Test started", 14);

    console.log(`Folder "${folderPath}" created successfully!`);
    return { success: true, folder: folderPath };
  } catch (error) {
    console.error("Error creating folder:", error);
    return { success: false, error: error.message };
  }
};
